import { Field, Menu, STATUSES, STATUS_HINT, STATUS_LABEL, formatDateTime, formatSoon, type IconName, type MenuItem, type MenuSection } from "@omnabase/ui";
import type { TicketStatus } from "@omnabase/shared";
import { SNOOZES } from "./ticketWords";

/** The ticket band's state menu (the top bar, signature and reports round, 2026-10-26; the status menu and snooze round, 2026-11-02, brief
 *  docs/briefs/status-menu-snooze-links-round.md): the Status field opens the one menu for where a ticket stands, in four groups: the statuses
 *  with their short second line, "Snooze until", Urgent, and "Move to" (Spam, Deleted). The band's clock button is gone. Also the sending
 *  options' statuses with the same second lines. The page owns what each choice does. */

type BandTicket = { status: TicketStatus; spam: boolean; urgent: boolean; snoozedUntil: string | null; deletedAt?: string | null };
export type StatusActions = {
  setStatus: (st: TicketStatus) => void; setUrgent: (on: boolean) => void; markSpam: () => void;
  snooze: (label: string, hours: number) => void; wake: () => void;
  /** set only while Deleted is offered (`useTicketDeletion`) */ remove?: () => void;
};

/** Snoozed means pending with the wake time still ahead: the server makes every snooze pending, and an old wake time stays behind when
 *  someone picks Resolved (the red team's findings 1 and 2; the intake and the waker read it the same way). */
export const snoozedUntilOf = (t: Pick<BandTicket, "status" | "snoozedUntil">, now: number = Date.now()): string | null =>
  t.status === "pending" && t.snoozedUntil && new Date(t.snoozedUntil).getTime() > now ? t.snoozedUntil : null;

/** a status as a menu entry: the label, its few words as the second line */
const statusItem = (st: TicketStatus, checked: boolean, onSelect: () => void): MenuItem => ({ id: st, label: STATUS_LABEL[st], hint: STATUS_HINT[st], checked, onSelect });

/** "Snooze until": on a snoozed ticket first the time it wakes (no live entry) and "Wake now", then the three choices */
function snoozeSection(snoozed: string | null, acts: StatusActions): MenuSection {
  const now: MenuItem[] = snoozed ? [{ id: "snoozed", label: `Snoozed until ${formatDateTime(snoozed)}`, disabled: true, onSelect: () => {} }, { id: "wake", label: "Wake now", icon: "bell", onSelect: acts.wake }] : [];
  return { label: "Snooze until", items: [...now, ...SNOOZES.map(([label, h]) => ({ id: `snooze-${label}`, label, icon: "clock" as const, onSelect: () => acts.snooze(label, h) }))] };
}

/** "Move to": Spam (ticked while spam, inert then: it is no status, "Not spam" puts the old one back) and Deleted for those who may delete
 *  (the 30 days stay in the toast and on the deleted ticket's strip) */
function moveSection(ticket: BandTicket, acts: StatusActions): MenuSection {
  return { label: "Move to", items: [
    { id: "spam", label: "Spam", icon: "spam", checked: ticket.spam, onSelect: () => { if (!ticket.spam) acts.markSpam(); } },
    ...(acts.remove ? [{ id: "delete", label: "Deleted", icon: "trash" as const, onSelect: acts.remove }] : []),
  ] };
}

/** The four groups in order: Status · Snooze until · Urgent · Move to. A ticket in Spam or Deleted gets no snooze (it is out of every queue). */
export function statusSections(ticket: BandTicket, acts: StatusActions, now: number = Date.now()): MenuSection[] {
  const away = ticket.spam || !!ticket.deletedAt;
  return [
    { label: "Status", items: STATUSES.map((st) => statusItem(st, !ticket.spam && st === ticket.status, () => acts.setStatus(st))) },
    ...(away ? [] : [snoozeSection(snoozedUntilOf(ticket, now), acts)]),
    { items: [{ id: "urgent", label: "Urgent", icon: "priorityHigh", checked: ticket.urgent, onSelect: () => acts.setUrgent(!ticket.urgent) }] },
    moveSection(ticket, acts),
  ];
}

/** The Status field's words: spam wins ("Spam"); a snoozed ticket reads "Snoozed · <short time>" with the clock and "Wakes <date and time>";
 *  otherwise the status with its second line as the hover. */
export function statusField(ticket: BandTicket, now: number = Date.now()): { value: string; icon: IconName; title: string } {
  if (ticket.spam) return { value: "Spam", icon: "spam", title: "Out of every view but Spam" };
  const snoozed = snoozedUntilOf(ticket, now);
  if (snoozed) return { value: `Snoozed · ${formatSoon(snoozed, now)}`, icon: "clock", title: `Wakes ${formatDateTime(snoozed)}` };
  return { value: STATUS_LABEL[ticket.status], icon: "circleDot", title: STATUS_HINT[ticket.status] };
}

/** the status button and its menu (the composer-and-fixes round, 2026-11-03: its hover is the kit's `data-tip`, as for every band button; the one menu allowed to grow to about 640 px, so Move to never hides below its fold); an urgent ticket
 *  carries the red "!" badge, and "urgent" is in the button's spoken name. On a band narrower than 560 px the snoozed field reads "Snoozed"
 *  alone (the time after the dot is the field's `tail`, which the band hides there). */
export function StatusMenu({ ticket, acts }: { ticket: BandTicket; acts: StatusActions }) {
  const f = statusField(ticket);
  const dot = f.value.indexOf(" · ");
  const [value, tail] = dot > 0 ? [f.value.slice(0, dot), f.value.slice(dot)] : [f.value, undefined];
  return <Menu tall trigger={<Field label="Status" icon={f.icon} value={value} tail={tail} data-tip={f.title} badge={ticket.urgent ? "urgent" : undefined} />} sections={statusSections(ticket, acts)} />;
}

/** the sending options' statuses ("After sending, set ticket to"), the same two-line entries as the status menu */
export const sendAsItems = (sendAs: TicketStatus, choose: (st: TicketStatus) => void): MenuItem[] => (["pending", "resolved", "open"] as TicketStatus[]).map((st) => statusItem(st, st === sendAs, () => choose(st)));
